import log from '@/lib/logger';
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/context/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "@/components/tailwind/dialog";
import { Button } from "@/components/tailwind/button";
import { Field, Label } from "@/components/tailwind/fieldset";
import { Input } from "@/components/tailwind/input";
import { Textarea } from "@/components/tailwind/textarea";
import { createLedgerEntry, updateLedgerEntry, getLedgerEntry } from "@/services/ledger-services";
import { Info, HelpCircle } from "lucide-react";
import { useLedgerTranslation } from "@/i18n/hooks";
import ValidationAlert from "@/components/validation-alert";
import { LedgerEntry } from "@/models/ledger";
import { ProblemDetail } from "@/types/problem-details";
import { Text } from "@/components/tailwind/text";

interface LedgerEntryDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    entryId?: string | null;
    mode: 'create' | 'edit';
}

type LedgerEntryFormData = {
    transactionDate: string;
    amount: string;
    description: string;
};

// Stable code attached by LedgerControllerAdvice; matching on it survives detail rewording.
const DATE_BEFORE_OPENING_BALANCE_CODE = "LEDGER_ENTRY_DATE_BEFORE_OPENING_BALANCE";

export function LedgerEntryDialog({ isOpen, onClose, onSuccess, entryId, mode }: LedgerEntryDialogProps) {
    const { token } = useAuth();
    const { toast } = useToast();
    const { t } = useLedgerTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [voucherNumber, setVoucherNumber] = useState<number | null>(null);
    const [loadedEntry, setLoadedEntry] = useState<LedgerEntry | null>(null);
    const [showAmountHelp, setShowAmountHelp] = useState(false);
    
    // Detect locale and thousand separator
    const locale = navigator.language || 'de-DE';
    const isGermanFormat = locale.startsWith('de');
    const thousandSeparator = isGermanFormat ? '.' : ',';

    const ledgerEntrySchema = z.object({
        transactionDate: z.string()
            .min(1, t('validation.dateRequired'))
            .refine((val) => {
                const selectedDate = new Date(val);
                const today = new Date();
                today.setHours(23, 59, 59, 999);
                return selectedDate <= today;
            }, t('validation.dateFuture')),
        amount: z.string()
            .min(1, t('validation.amountRequired'))
            // Thousand separators are not allowed.
            .refine((val) => !val.includes(thousandSeparator),
                t('validation.amountNoThousandSeparator', { separator: thousandSeparator }))
            .refine((val) => {
                // Convert locale-specific decimal separator to standard format
                const normalizedVal = isGermanFormat ? val.replace(',', '.') : val;
                const num = parseFloat(normalizedVal);
                return !isNaN(num) && num !== 0;
            }, t('validation.amountInvalid')),
        description: z.string()
            .min(1, t('validation.descriptionRequired'))
            .max(2000, t('validation.descriptionTooLong'))
    });

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors }
    } = useForm<LedgerEntryFormData>({
        resolver: zodResolver(ledgerEntrySchema),
        defaultValues: {
            transactionDate: new Date().toISOString().split('T')[0],
            amount: '',
            description: ''
        }
    });

    // Load entry data when in edit mode
    useEffect(() => {
        if (!isOpen) {
            // Dialog closed, reset state
            setIsLoading(false);
            setLoadedEntry(null);
            setVoucherNumber(null);
            setShowAmountHelp(false);
            return;
        }

        if (mode === 'edit' && entryId && token) {
            // Only load if we haven't loaded this entry yet
            if (loadedEntry?.id !== entryId) {
                setIsLoading(true);
                getLedgerEntry(token, entryId)
                    .then((entry: LedgerEntry) => {
                        setLoadedEntry(entry);
                        setVoucherNumber(entry.voucherNumber);
                        setValue('transactionDate', entry.transactionDate);
                        
                        // Format amount according to locale
                        const amountStr = entry.amount.toString();
                        const formattedAmount = isGermanFormat 
                            ? amountStr.replace('.', ',')
                            : amountStr;
                        setValue('amount', formattedAmount);
                        setValue('description', entry.description);
                        setIsLoading(false);
                    })
                    .catch((error) => {
                        log.error("Error loading entry:", error);
                        toast({
                            title: t('messages.error.title'),
                            description: error?.detail || t('messages.error.loadFailed'),
                            variant: "destructive"
                        });
                        setIsLoading(false);
                        // Don't close the dialog, let the user close it manually
                    });
            }
        }
    }, [mode, entryId, token, isOpen, setValue, toast, t, isGermanFormat, loadedEntry]);
    
    // Reset form when switching to create mode
    useEffect(() => {
        if (isOpen && mode === 'create') {
            reset({
                transactionDate: new Date().toISOString().split('T')[0],
                amount: '',
                description: ''
            });
            setVoucherNumber(null);
            setLoadedEntry(null);
            setIsLoading(false);
        }
    }, [isOpen, mode, reset]);

    const handleClose = () => {
        reset();
        setVoucherNumber(null);
        setLoadedEntry(null);
        setIsLoading(false);
        setShowAmountHelp(false);
        onClose();
    };

    const onSubmit = async (data: LedgerEntryFormData) => {
        if (!token) return;

        setIsSubmitting(true);
        try {
            // Convert locale-specific format to standard format for parsing
            const normalizedAmount = isGermanFormat ? data.amount.replace(',', '.') : data.amount;
            const amount = parseFloat(normalizedAmount);
            
            const entryData = {
                transactionDate: data.transactionDate,
                amount,
                description: data.description
            };

            if (mode === 'edit' && entryId) {
                await updateLedgerEntry(token, entryId, entryData);
                toast({
                    title: t('messages.success.title'),
                    description: t('messages.success.updated'),
                    variant: "default"
                });
            } else {
                await createLedgerEntry(token, entryData);
                toast({
                    title: t('messages.success.title'),
                    description: t('messages.success.created'),
                    variant: "default"
                });
            }

            reset();
            onSuccess();
        } catch (error) {
            log.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} ledger entry:`, error);
            const code = (error as ProblemDetail | null)?.code;
            const fallback = mode === 'edit'
                ? t('messages.error.updateFailed')
                : t('messages.error.createFailed');
            toast({
                title: t('messages.error.title'),
                description: code === DATE_BEFORE_OPENING_BALANCE_CODE
                    ? t('messages.error.dateBeforeOpeningBalance')
                    : fallback,
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={handleClose}>
            <DialogTitle>
                {mode === 'edit' ? t('dialog.edit.title') : t('dialog.create.title')}
            </DialogTitle>
            <DialogDescription>
                {mode === 'edit' ? t('dialog.edit.description') : t('dialog.create.description')}
            </DialogDescription>
            
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody className="space-y-4">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <Text className="text-gray-500">{t('dialog.loading')}</Text>
                        </div>
                    ) : (
                        <>
                            {mode === 'edit' && voucherNumber !== null && (
                                <div className="flex items-center gap-2 pb-2">
                                    <Text className="text-sm font-medium text-zinc-950 dark:text-white">
                                        {t('fields.voucherNumber')}:
                                    </Text>
                                    <Text className="text-sm font-semibold">
                                        {voucherNumber}
                                    </Text>
                                </div>
                            )}

                            <Field>
                                <Label>{t('fields.transactionDate')}</Label>
                                <Input
                                    type="date"
                                    {...register("transactionDate")}
                                    disabled={isSubmitting}
                                    invalid={!!errors.transactionDate}
                                />
                                {errors.transactionDate && (
                                    <ValidationAlert message={errors.transactionDate.message || ''} />
                                )}
                            </Field>

                            <Field>
                                <div className="flex items-center gap-2">
                                    <Label>{t('fields.amount')}</Label>
                                    <button
                                        type="button"
                                        onClick={() => setShowAmountHelp(!showAmountHelp)}
                                        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                                        aria-label={showAmountHelp ? "Hilfe ausblenden" : "Hilfe anzeigen"}
                                    >
                                        <HelpCircle className="h-4 w-4" />
                                    </button>
                                </div>
                                <Input
                                    type="text"
                                    placeholder={isGermanFormat ? "z.B. 150,00 oder -75,50" : "e.g. 150.00 or -75.50"}
                                    {...register("amount")}
                                    disabled={isSubmitting}
                                    invalid={!!errors.amount}
                                />
                                {errors.amount && (
                                    <ValidationAlert message={errors.amount.message || ''} />
                                )}
                                {showAmountHelp && (
                                    <div className="mt-2 bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-3 rounded">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <Info className="h-4 w-4 text-blue-400 dark:text-blue-500" />
                                            </div>
                                            <div className="ml-2">
                                                <p className="text-xs text-blue-700 dark:text-blue-300">
                                                    {t('dialog.create.hintText')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Field>

                            <Field>
                                <Label>{t('fields.description')}</Label>
                                <Textarea
                                    rows={3}
                                    placeholder={t('placeholders.description')}
                                    maxLength={2000}
                                    {...register("description")}
                                    disabled={isSubmitting}
                                    invalid={!!errors.description}
                                />
                                {errors.description && (
                                    <ValidationAlert message={errors.description.message || ''} />
                                )}
                            </Field>
                        </>
                    )}
                </DialogBody>

                <DialogActions>
                    <Button plain onClick={handleClose} disabled={isSubmitting || isLoading}>
                        {t('actions.cancel')}
                    </Button>
                    <Button type="submit" disabled={isSubmitting || isLoading}>
                        {isSubmitting ? t('actions.saving') : t('actions.save')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}